// RUTA: src/components/fitness/MealPlanSection.tsx

import { useState } from 'react';
import { UtensilsCrossed, CheckCircle2, Circle, Loader2, AlarmClock, Pencil, X, Trash2, ChevronUp, ChevronDown, ShoppingCart } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { ScrollHintRow } from './ScrollHintRow';
import { ShoppingListDialog } from './ShoppingListDialog';
import { Meal } from '@/types/fitness';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { getTodayLocalString, getTodayDayOfWeek } from '@/lib/dateUtils';

interface MealPlanSectionProps {
  plan: Meal[];
  activeUser: string;
  userId?: string;
  onCaloriesLogged?: () => void;
}

// ─── Custom time picker (desktop only) ───────────────────────────────────────

const HOURS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function TimePickerPopover({
  value,
  onConfirm,
  onClose,
  onDelete,
}: {
  value: string;
  onConfirm: (time: string) => void;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const parse = (v: string) => {
    if (!v) return { h: 8, m: 0 };
    const [hStr, mStr] = v.split(':');
    return { h: parseInt(hStr) || 8, m: parseInt(mStr) || 0 };
  };

  const { h, m } = parse(value);
  const [hour, setHour] = useState(h % 12 || 12);
  const [minute, setMinute] = useState(Math.round(m / 5) * 5 % 60);
  const [period, setPeriod] = useState<'AM' | 'PM'>(h >= 12 ? 'PM' : 'AM');

  const stepHour = (delta: number) =>
    setHour(prev => ((prev - 1 + delta + 12) % 12) + 1);

  const stepMinute = (delta: number) => {
    const idx = MINUTES.indexOf(minute);
    const next = (idx + delta + MINUTES.length) % MINUTES.length;
    setMinute(MINUTES[next]);
  };

  const confirm = () => {
    let h24 = hour === 12 ? 0 : hour;
    if (period === 'PM') h24 += 12;
    onConfirm(`${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
  };

  const colBtn = 'w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent/20 transition-colors text-muted-foreground hover:text-accent';

  return (
    <>
      {/* Click-outside overlay */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      {/* Picker panel */}
      <div className="absolute z-50 left-0 right-0 top-full mt-2 bg-card border-2 border-accent/40 rounded-2xl shadow-2xl p-5 select-none">
        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground text-center mb-4">
          Selecciona el horario
        </p>

        <div className="flex items-center justify-center gap-3">

          {/* Hours column */}
          <div className="flex flex-col items-center gap-1 w-16">
            <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Hora</p>
            <button onClick={() => stepHour(1)} className={colBtn}><ChevronUp className="h-4 w-4" /></button>
            <div className="w-16 h-12 flex items-center justify-center bg-accent/10 border-2 border-accent rounded-xl">
              <span className="text-2xl font-black text-foreground">{String(hour).padStart(2, '0')}</span>
            </div>
            <button onClick={() => stepHour(-1)} className={colBtn}><ChevronDown className="h-4 w-4" /></button>
          </div>

          <span className="text-2xl font-black text-muted-foreground pb-1">:</span>

          {/* Minutes column */}
          <div className="flex flex-col items-center gap-1 w-16">
            <p className="text-[9px] font-black uppercase tracking-widest text-muted-foreground mb-1">Min</p>
            <button onClick={() => stepMinute(1)} className={colBtn}><ChevronUp className="h-4 w-4" /></button>
            <div className="w-16 h-12 flex items-center justify-center bg-accent/10 border-2 border-accent rounded-xl">
              <span className="text-2xl font-black text-foreground">{String(minute).padStart(2, '0')}</span>
            </div>
            <button onClick={() => stepMinute(-1)} className={colBtn}><ChevronDown className="h-4 w-4" /></button>
          </div>

          {/* AM / PM */}
          <div className="flex flex-col gap-2 ml-1 mt-5">
            {(['AM', 'PM'] as const).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`w-14 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${
                  period === p
                    ? 'bg-accent text-accent-foreground shadow-sm'
                    : 'bg-secondary text-muted-foreground hover:bg-border'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <p className="text-center text-sm font-black text-accent mt-4">
          {String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')} {period}
        </p>

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl bg-secondary text-xs font-black uppercase tracking-widest text-muted-foreground hover:bg-border transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            className="flex-1 py-2.5 rounded-xl bg-accent text-accent-foreground text-xs font-black uppercase tracking-widest hover:bg-accent/90 transition-colors"
          >
            Listo ✓
          </button>
        </div>

        {/* Delete option */}
        {onDelete && (
          <button
            onClick={onDelete}
            className="flex items-center justify-center gap-2 w-full mt-2 py-2 rounded-xl bg-destructive/10 border border-destructive/30 hover:bg-destructive/20 transition-colors text-destructive text-xs font-bold"
          >
            <Trash2 className="h-3.5 w-3.5" /> Eliminar horario
          </button>
        )}
      </div>
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MealPlanSection({ plan, activeUser, userId, onCaloriesLogged }: MealPlanSectionProps) {
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [shoppingListOpen, setShoppingListOpen] = useState(false);

  // Day chips — defaults to today's day of the week. Mirrors the
  // RoutineSection pattern so the user has the same mental model.
  const [selectedDay, setSelectedDay] = useState<string>(() => getTodayDayOfWeek());

  // Compute which days are actually represented in the plan. Plans
  // imported from PDF have one row per day; legacy plans without
  // day_of_week (NULL) apply to every day, so we still show every day
  // chip but they all render the same content.
  const ALL_DAYS = ['lunes','martes','miercoles','jueves','viernes','sabado','domingo'];
  const DAY_LABELS_SHORT: Record<string, string> = {
    lunes: 'Lun', martes: 'Mar', miercoles: 'Mié',
    jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom',
  };
  const planHasDayVariation = plan.some(m => m.dayOfWeek);
  const visibleDays = planHasDayVariation
    ? ALL_DAYS.filter(d => plan.some(m => m.dayOfWeek === d || !m.dayOfWeek))
    : ALL_DAYS;

  // Filter the plan for the day the user is looking at. Meals with
  // dayOfWeek == null apply to every day.
  const filteredPlan = planHasDayVariation
    ? plan.filter(m => !m.dayOfWeek || m.dayOfWeek === selectedDay)
    : plan;

  // For days with per-day variation, figure out which meal slots the
  // selected day is MISSING relative to the rest of the week. The
  // nutritionist sometimes leaves a slot blank on purpose ("Sin receta
  // seleccionada"); without an explicit note the client may think it's
  // a bug. We surface a friendly message instead.
  const MEAL_LABELS_FRIENDLY: Record<string, string> = {
    desayuno: 'Desayuno',
    colacion1: 'Colación 1',
    comida: 'Comida',
    colacion2: 'Colación 2',
    cena: 'Cena',
    snack: 'Snack',
  };
  const missingMealsForDay: string[] = (() => {
    if (!planHasDayVariation) return [];
    const allMealTimes = new Set(plan.map(m => m.nombre));
    const dayMealTimes = new Set(filteredPlan.map(m => m.nombre));
    return Array.from(allMealTimes).filter(mt => !dayMealTimes.has(mt));
  })();

  // En modo "por día" (lun-mié-vie por ejemplo) hay días sin plan. Si
  // hoy es uno de esos, computamos el siguiente día con plan para
  // mostrarlo en el estado vacío.
  const isDayMissingFromPlan = planHasDayVariation && filteredPlan.length === 0;
  const nextPlannedDay = (() => {
    if (!isDayMissingFromPlan) return null;
    const todayIdx = ALL_DAYS.indexOf(selectedDay);
    for (let i = 1; i <= 7; i++) {
      const candidate = ALL_DAYS[(todayIdx + i) % 7];
      if (visibleDays.includes(candidate)) return candidate;
    }
    return null;
  })();

  const [clientTimes, setClientTimes] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    plan.forEach(m => { if (m.clientTime) initial[m.nombre] = m.clientTime; });
    return initial;
  });

  const [completed, setCompleted] = useState<Record<string, boolean>>(() => {
    const today = getTodayLocalString();
    const initial: Record<string, boolean> = {};
    plan.forEach(m => { initial[m.nombre] = m.completedDate === today; });
    return initial;
  });

  // State conservado por si reactivas `calculateAndLogCalories` con un
  // flag por cliente. Hoy nadie lo setea a true porque el flujo de
  // calcular calorías automáticas se deshabilitó.
  const [loadingCalories] = useState<Record<string, boolean>>({});
  const [editingTime, setEditingTime] = useState<string | null>(null);
  const [hiddenCoachTimes, setHiddenCoachTimes] = useState<Record<string, boolean>>({});

  const today = getTodayLocalString();

  const saveClientTime = async (mealNombre: string, time: string) => {
    if (!userId) return;
    setClientTimes(prev => {
      if (!time) {
        const next = { ...prev };
        delete next[mealNombre];
        return next;
      }
      return { ...prev, [mealNombre]: time };
    });
    try {
      await supabase
        .from('client_meal_plans')
        .update({ client_time: time || null })
        .eq('client_id', userId)
        .eq('meal_time', mealNombre)
        .eq('is_active', true);
      if (!time) toast.success('Horario eliminado');
    } catch (error) {
      console.error('Error saving client time:', error);
    }
  };

  const toggleComplete = async (meal: Meal) => {
    if (!userId) return;
    const isCompleted = completed[meal.nombre];
    const newCompleted = !isCompleted;

    setCompleted(prev => ({ ...prev, [meal.nombre]: newCompleted }));

    try {
      await supabase
        .from('client_meal_plans')
        .update({ completed_date: newCompleted ? today : null })
        .eq('client_id', userId)
        .eq('meal_time', meal.nombre)
        .eq('is_active', true);

      // Solo marcamos cumplimiento; el cliente registra calorías por su
      // cuenta desde la sección "Nutrición". La llamada automática a
      // calculateAndLogCalories se quitó porque (a) saturaba al cliente
      // con calorías que quizá no comió tal cual el plan, (b) gasta IA
      // (Gemini) en cada marcado, (c) muchos coaches solo quieren saber
      // si el cliente cumplió la comida, no auto-cargar el food_log.
      // La función calculateAndLogCalories se conserva más abajo
      // comentada por si se reactiva con un flag por cliente.
      if (newCompleted) {
        toast.success(`✅ ${meal.label || meal.nombre} marcada`);
      } else {
        toast.info(`↩️ ${meal.label || meal.nombre} desmarcada`);
      }
    } catch (error) {
      console.error('Error toggling complete:', error);
      setCompleted(prev => ({ ...prev, [meal.nombre]: isCompleted }));
    }
  };

  /* DESHABILITADA — ver toggleComplete arriba para la razón.
     La conservamos en código comentado por si en el futuro la
     reactivas con un flag por cliente o por coach.

  const calculateAndLogCalories = async (meal: Meal) => {
    if (!userId || !meal.items.length) return;
    setLoadingCalories(prev => ({ ...prev, [meal.nombre]: true }));

    try {
      // Bug 2 fix: usar todos los items como texto (rawText solo tenía el primero)
      const foodText = meal.items.join('\n');

      // Bug 1 fix: nombre correcto de la edge function (era 'analizar-alimentos')
      const { data, error } = await supabase.functions.invoke('analyze-food', {
        body: { text: foodText },
      });

      if (error || !data?.items?.length) {
        console.error('Edge function error:', error);
        toast.error('No se pudieron calcular las calorías');
        return;
      }

      const items: any[] = data.items;
      const totalCalories = Math.round(items.reduce((s, i) => s + (i.calories || 0), 0));
      const totalProtein = Math.round(items.reduce((s, i) => s + (i.protein || 0), 0));
      const totalCarbs = Math.round(items.reduce((s, i) => s + (i.carbs || 0), 0));
      const totalFat = Math.round(items.reduce((s, i) => s + (i.fat || 0), 0));
      const description = items.map(i => i.item).join(', ');

      // Bug 4 fix: eliminar log previo del mismo día para evitar duplicados al re-marcar
      await supabase
        .from('food_logs')
        .delete()
        .eq('user_id', userId)
        .eq('log_date', today)
        .eq('meal_type', getMealType(meal.nombre))
        .eq('profile_name', activeUser);

      await supabase.from('food_logs').insert({
        user_id: userId,
        item_name: description || (meal.label || meal.nombre),
        calories: totalCalories,
        meal_type: getMealType(meal.nombre),
        log_date: today,
        profile_name: activeUser,
        protein: totalProtein,
        carbs: totalCarbs,
        fat: totalFat,
        portion_amount: 1,
        portion_unit: 'porción',
      });

      toast.success(`🔥 +${totalCalories} kcal registradas en tu contador`);
      onCaloriesLogged?.();
    } catch (error) {
      console.error('Error calculating calories:', error);
      toast.error('Error al calcular calorías');
    } finally {
      setLoadingCalories(prev => ({ ...prev, [meal.nombre]: false }));
    }
  };
  */

  const getMealType = (mealNombre: string): string => {
    if (mealNombre === 'breakfast') return 'desayuno';
    if (mealNombre === 'lunch') return 'comida';
    if (mealNombre === 'dinner') return 'cena';
    if (mealNombre.startsWith('snack')) return 'snack';
    if (mealNombre.startsWith('meal_')) return 'comida';
    return 'comida';
  };

  const formatTime = (time?: string) => {
    if (!time || time === '00:00') return null;
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  return (
    <section id="plan" className="scroll-mt-32">
      {/* Header consistente con el resto de las secciones (Nutrición &
          Calorías, Rutina, etc.). Antes había una barra sticky custom
          con el título chiquito al lado de los chips — se sentía
          distinto al resto y rompía el design system. Ahora el
          SectionHeader grande va arriba SIEMPRE y la barra sticky
          queda solo con los chips de día + botón Súper. */}
      <SectionHeader title="Plan Alimenticio" subtitle={activeUser} icon={UtensilsCrossed} helpId="plan-alimenticio" />

      {/* CTA del súper — usamos el mismo patrón visual de BookTrainingCard
          ("¿Listo para subir el nivel?") pero SIN el border punteado
          rojo (lo quitamos a propósito), texto centrado, conservando
          ícono carrito y flecha. */}
      {plan.length > 0 && userId && (
        <button
          onClick={() => setShoppingListOpen(true)}
          className="w-full mb-4 md:mb-6 group flex items-center gap-4 p-4 md:p-5 rounded-2xl bg-gradient-to-br from-accent/15 via-accent/8 to-transparent dark:from-accent/20 dark:via-accent/10 dark:to-transparent hover:from-accent/20 hover:via-accent/10 transition-all"
        >
          <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-xl bg-accent/25 flex items-center justify-center">
            <ShoppingCart className="w-7 h-7 md:w-8 md:h-8 text-accent" />
          </div>
          <div className="flex-1 text-center min-w-0 px-2">
            <div className="text-xs md:text-sm font-black uppercase tracking-wider text-foreground dark:text-primary-foreground leading-snug">
              ¿No sabes qué necesitas comprar en el súper esta semana?
            </div>
            <div className="text-xs md:text-sm text-muted-foreground dark:text-primary-foreground/70 mt-1.5 leading-snug">
              Haz click aquí y te lo calculamos según tu plan alimenticio.
            </div>
          </div>
          <span className="shrink-0 text-accent text-2xl md:text-3xl font-black group-hover:translate-x-1 transition-transform">
            →
          </span>
        </button>
      )}

      {/* Sticky bar SOLO con chips de día — cuando el plan varía por día */}
      {plan.length > 0 && planHasDayVariation && (
        <div className="sticky top-[60px] z-30 bg-background/95 backdrop-blur-sm border-y border-border -mx-3 md:mx-0 px-3 md:px-4 py-3 mb-4 md:mb-6">
          <ScrollHintRow className="-mx-1 px-1">
          <div className="flex gap-1.5">
            {visibleDays.map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDay(d)}
                className={`px-3 py-2 rounded-lg font-black uppercase tracking-wider text-[11px] whitespace-nowrap transition-all min-h-[40px] shrink-0 ${
                  selectedDay === d
                    ? 'bg-primary text-accent shadow-lg'
                    : 'bg-secondary text-muted-foreground hover:bg-border'
                }`}
              >
                {DAY_LABELS_SHORT[d]}
              </button>
            ))}
          </div>
          </ScrollHintRow>
        </div>
      )}

      {/* Modo "por día" en un día sin plan (ej. martes en plan lun-mié-vie).
          Sin esto el cliente vería una pantalla vacía y pensaría que
          se rompió algo. */}
      {isDayMissingFromPlan && (
        <div className="rounded-2xl border-2 border-dashed border-border bg-secondary/20 p-6 text-center">
          <p className="text-base font-bold mb-1">
            🛌 No hay plan alimenticio para {DAY_LABELS_SHORT[selectedDay] || selectedDay}
          </p>
          <p className="text-sm text-muted-foreground">
            {nextPlannedDay
              ? <>Tu próximo día con plan es <strong>{DAY_LABELS_SHORT[nextPlannedDay] || nextPlannedDay}</strong>.</>
              : 'Tu coach aún no ha asignado plan para esta semana.'}
          </p>
          {nextPlannedDay && (
            <button
              onClick={() => setSelectedDay(nextPlannedDay)}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent text-accent-foreground text-sm font-bold hover:opacity-90 transition-opacity"
            >
              Ver plan de {DAY_LABELS_SHORT[nextPlannedDay] || nextPlannedDay}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPlan.map((meal, i) => {
          const isCompleted = completed[meal.nombre];
          const clientTime = clientTimes[meal.nombre];
          const coachTime = formatTime(meal.coachTime);
          const displayCoachTime = hiddenCoachTimes[meal.nombre] ? null : coachTime;
          const isLoadingCal = loadingCalories[meal.nombre];
          const isEditingThis = editingTime === meal.nombre;

          const handleDelete = () => {
            if (clientTime) {
              saveClientTime(meal.nombre, '');
            } else if (displayCoachTime) {
              setHiddenCoachTimes(prev => ({ ...prev, [meal.nombre]: true }));
              toast.success('Horario eliminado');
            }
            setEditingTime(null);
          };

          return (
            <div
              key={i}
              className={`bg-card rounded-2xl overflow-hidden shadow-sm transition-all duration-300 ${
                isCompleted
                  ? 'border-2 border-green-400/50 shadow-green-400/10 shadow-md opacity-85'
                  : 'border border-border hover:border-accent/40 hover:shadow-accent/5 hover:shadow-md'
              }`}
            >
              {/* ── Header ── */}
              <div className={`relative px-5 py-4 flex items-center justify-between overflow-hidden ${
                isCompleted
                  ? 'bg-gradient-to-r from-green-700 to-green-600'
                  : 'bg-gradient-to-r from-primary to-primary/90'
              }`}>
                {/* Left accent stripe */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${isCompleted ? 'bg-green-300/60' : 'bg-accent'}`} />

                <div className="flex items-center gap-3 pl-1">
                  {/* Meal number badge */}
                  <span className={`text-[10px] font-black tabular-nums px-2 py-0.5 rounded-full ${
                    isCompleted ? 'bg-green-500/40 text-green-100' : 'bg-accent/20 text-accent'
                  }`}>
                    #{i + 1}
                  </span>
                  <h4 className={`font-black uppercase tracking-widest text-sm ${
                    isCompleted ? 'text-green-100' : 'text-accent'
                  }`}>
                    {meal.label || meal.nombre}
                  </h4>
                </div>

                {isCompleted ? (
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4 text-green-200" />
                    <span className="text-[10px] font-black text-green-200 uppercase tracking-wider">Completada</span>
                  </div>
                ) : (
                  <span className="text-[10px] font-bold text-muted-foreground">
                    {meal.items.length} alimentos
                  </span>
                )}
              </div>

              {/* ── Horario ── */}
              <div className="px-4 pt-4 pb-2 relative">
                {/* Coach time chip (compact, above the button) */}
                {displayCoachTime && !clientTime && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <AlarmClock className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">Sugerido:</span>
                    <span className="text-[10px] font-black text-foreground">{displayCoachTime}</span>
                  </div>
                )}

                {/* Mobile: native input inline */}
                {isEditingThis && isMobile && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={clientTime || ''}
                        autoFocus
                        onChange={e => { saveClientTime(meal.nombre, e.target.value); setEditingTime(null); }}
                        className="flex-1 h-11 rounded-xl border-2 border-accent bg-background px-3 text-sm font-bold text-foreground outline-none focus:ring-2 focus:ring-accent/40"
                      />
                      <button
                        onClick={() => setEditingTime(null)}
                        className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary hover:bg-border transition-colors shrink-0"
                      >
                        <X className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </div>
                    {(clientTime || displayCoachTime) && (
                      <button
                        onClick={handleDelete}
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-xl bg-destructive/10 border border-destructive/30 hover:bg-destructive/20 transition-colors text-destructive text-xs font-bold"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Eliminar horario
                      </button>
                    )}
                  </div>
                )}

                {/* Desktop: custom picker popover */}
                {isEditingThis && !isMobile && (
                  <TimePickerPopover
                    value={clientTime || ''}
                    onConfirm={time => { saveClientTime(meal.nombre, time); setEditingTime(null); }}
                    onClose={() => setEditingTime(null)}
                    onDelete={(clientTime || displayCoachTime) ? handleDelete : undefined}
                  />
                )}

                {/* Trigger button (shown when not editing) */}
                {!isEditingThis && (
                  <div className="flex items-stretch gap-2">
                    <button
                      onClick={() => setEditingTime(meal.nombre)}
                      className={`flex items-center gap-3 flex-1 px-4 py-2.5 rounded-xl transition-all text-left group min-h-[48px] ${
                        clientTime
                          ? 'bg-accent/10 border border-accent/40 hover:bg-accent/15'
                          : 'bg-secondary hover:bg-muted border border-dashed border-border hover:border-accent/40'
                      }`}
                    >
                      <AlarmClock className={`h-4 w-4 shrink-0 ${clientTime ? 'text-accent' : 'text-muted-foreground group-hover:text-accent'} transition-colors`} />
                      <span className="flex-1 min-w-0">
                        {clientTime ? (
                          <>
                            <span className="block text-[9px] font-black uppercase tracking-wider text-accent/80">Mi horario</span>
                            <span className="block text-sm font-black text-foreground mt-0.5">{formatTime(clientTime)}</span>
                          </>
                        ) : (
                          <span className="block text-xs font-semibold text-muted-foreground group-hover:text-foreground transition-colors">
                            {displayCoachTime ? 'Personalizar horario' : 'Agregar mi horario'}
                          </span>
                        )}
                      </span>
                      <Pencil className={`h-3 w-3 shrink-0 ${clientTime ? 'text-accent' : 'text-muted-foreground/50 group-hover:text-accent'} transition-colors`} />
                    </button>

                    {(clientTime || displayCoachTime) && (
                      <button
                        onClick={handleDelete}
                        title="Eliminar horario"
                        className="w-10 flex items-center justify-center rounded-xl bg-secondary hover:bg-destructive/10 border border-dashed border-border hover:border-destructive/40 transition-all shrink-0 group/del"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground group-hover/del:text-destructive transition-colors" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ── Divider ── */}
              <div className="mx-4 my-3 h-px bg-border/60" />

              {/* ── Items ── */}
              <ul className={`px-4 pb-3 space-y-2 ${isCompleted ? 'opacity-50' : ''}`}>
                {meal.items.map((item, j) => (
                  <li key={j} className={`flex items-start gap-2.5 text-sm ${isCompleted ? 'line-through' : ''}`}>
                    <span className="mt-[5px] shrink-0 w-1.5 h-1.5 rounded-full bg-accent/70" />
                    <span className="font-medium text-foreground/90 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>

              {/* ── Nota especial ── */}
              {meal.specialInstructions && (
                <div className="mx-4 mb-3 px-3 py-2 rounded-lg bg-muted/60 border border-border/60">
                  <p className="text-[11px] text-muted-foreground italic leading-snug">
                    📝 {meal.specialInstructions}
                  </p>
                </div>
              )}

              {/* ── Botón Ya comí ── */}
              {/* Solo marca cumplimiento de la comida. NO suma calorías
                  al contador del día — eso lo hace el cliente desde la
                  sección "Nutrición" cuando quiere. Si reactivas el flujo
                  automático (ver calculateAndLogCalories), restaura el
                  estado "Calculando calorías..." aquí. */}
              <div className="px-4 pb-4">
                <button
                  onClick={() => toggleComplete(meal)}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                    isCompleted
                      ? 'bg-green-500/20 text-green-700 dark:text-green-400 border-2 border-green-400/50 hover:bg-green-500/30'
                      : 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm shadow-accent/20'
                  }`}
                >
                  {isCompleted ? (
                    <>
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Ya comí esto ✓
                    </>
                  ) : (
                    <>
                      <Circle className="h-3.5 w-3.5" />
                      Marcar como comida
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Friendly note when the nutritionist intentionally left some
          slots blank for the selected day (PDF says "Sin receta
          seleccionada"). Without this, the client may worry the plan
          loaded incorrectly. */}
      {planHasDayVariation && missingMealsForDay.length > 0 && (
        <div className="mt-6 rounded-xl border-2 border-dashed border-border bg-secondary/20 p-4 text-center">
          <p className="text-sm font-bold mb-1">
            🌙 {DAY_LABELS_SHORT[selectedDay] || selectedDay} —
            sin {missingMealsForDay.length === 1 ? 'comida' : 'comidas'} planeada
            {missingMealsForDay.length === 1 ? '' : 's'} en{' '}
            {missingMealsForDay
              .map(mt => MEAL_LABELS_FRIENDLY[mt] || mt)
              .join(', ')}
          </p>
          <p className="text-xs text-muted-foreground">
            Tu nutrióloga no asignó {missingMealsForDay.length === 1 ? 'esta comida' : 'estas comidas'} para este día.
            Si crees que es un error, pregúntale a tu coach.
          </p>
        </div>
      )}

      {userId && (
        <ShoppingListDialog
          open={shoppingListOpen}
          onOpenChange={setShoppingListOpen}
          clientId={userId}
          title={`Mi plan — ${activeUser}`}
        />
      )}
    </section>
  );
}
